import { FaTasks, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaFilter, FaSort, FaCalendarDay, FaUser, FaCheckCircle, FaClock, FaExclamationCircle, FaLink } from "react-icons/fa";
import { useState } from "react";
import { useAuth } from "./AuthContext";

export default function ActionItems() {
  const { user } = useAuth();
  const isAdmin = user?.isProfileAdmin || false;

  const [actionItems, setActionItems] = useState([
    {
      id: 1,
      title: "Review budget proposals from all committees",
      description: "Analyze and provide feedback on Q1 budget submissions from Academic Staff, Researchers, Communications, HR, and Belonging committees.",
      assignedTo: "Josie Simm",
      assignedEmail: "js9640@nyu.edu",
      committee: "Steering Committee",
      status: "In Progress",
      priority: "High",
      dueDate: "2026-01-20",
      createdDate: "2026-01-02",
      linkedMeeting: "Steering Committee Meeting - Jan 2, 2026",
      progressNotes: [
        { date: "2026-01-04", note: "Received proposals from 3 committees so far", author: "Josie Simm" },
      ],
    },
    {
      id: 2,
      title: "Newsletter content submission",
      description: "Gather updates from all committees for January newsletter. Include highlights from December meetings and upcoming events.",
      assignedTo: "Eudora Okine",
      assignedEmail: "eo50@nyu.edu",
      committee: "Communications",
      status: "Overdue",
      priority: "Urgent",
      dueDate: "2026-01-10",
      createdDate: "2025-12-28",
      linkedMeeting: "Communications Committee Meeting - Dec 28, 2025",
      progressNotes: [],
    },
    {
      id: 3,
      title: "Distribute lab safety guidelines",
      description: "Work with relevant departments to finalize and distribute updated lab safety guidelines to all research staff.",
      assignedTo: "Anand Kant Das",
      assignedEmail: "akd6@nyu.edu",
      committee: "Researchers",
      status: "In Progress",
      priority: "High",
      dueDate: "2026-01-10",
      createdDate: "2026-01-03",
      linkedMeeting: "Researchers Committee Meeting - Jan 3, 2026",
      progressNotes: [
        { date: "2026-01-03", note: "Met with safety officer to review guidelines", author: "Anand Kant Das" },
      ],
    },
    {
      id: 4,
      title: "Coordinate professional development workshops",
      description: "Partner with HR to organize workshop series based on staff survey results.",
      assignedTo: "Karol Tolpa",
      assignedEmail: "kjt9854@nyu.edu",
      committee: "Academic Staff",
      status: "Not Started",
      priority: "Medium",
      dueDate: "2026-01-15",
      createdDate: "2026-01-04",
      linkedMeeting: "Academic Staff Committee Meeting - Jan 4, 2026",
      progressNotes: [],
    },
    {
      id: 5,
      title: "Plan diversity and inclusion workshop",
      description: "Finalize workshop agenda, secure venue, and send out invitations with RSVP link.",
      assignedTo: "Amnah Khan",
      assignedEmail: "ask9190@nyu.edu",
      committee: "Belonging",
      status: "In Progress",
      priority: "High",
      dueDate: "2026-01-25",
      createdDate: "2026-01-04",
      linkedMeeting: "Belonging Committee Meeting - Jan 4, 2026",
      progressNotes: [
        { date: "2026-01-04", note: "Venue booked for January 25th", author: "Amnah Khan" },
      ],
    },
  ]);

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showProgressNote, setShowProgressNote] = useState(null);
  const [progressNoteText, setProgressNoteText] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignedTo: "",
    assignedEmail: "",
    committee: "",
    status: "Not Started",
    priority: "Medium",
    dueDate: "",
    linkedMeeting: "",
  });

  // Filters
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [filterCommittee, setFilterCommittee] = useState("All");
  const [filterAssignee, setFilterAssignee] = useState("All");
  const [sortBy, setSortBy] = useState("dueDate"); // dueDate, priority, status, created

  const statuses = ["Not Started", "In Progress", "Completed", "Blocked", "Overdue"];
  const priorities = ["Low", "Medium", "High", "Urgent"];
  const committees = ["Steering Committee", "Chair", "Vice Chair", "Academic Staff", "Researchers", "Communications", "HR", "Belonging", "OSG", "Governance Liaison Officer"];

  const getUniqueAssignees = () => {
    return [...new Set(actionItems.map(item => item.assignedTo))];
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      title: "",
      description: "",
      assignedTo: "",
      assignedEmail: "",
      committee: "",
      status: "Not Started",
      priority: "Medium",
      dueDate: "",
      linkedMeeting: "",
    });
    setShowForm(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      assignedTo: item.assignedTo,
      assignedEmail: item.assignedEmail,
      committee: item.committee,
      status: item.status,
      priority: item.priority,
      dueDate: item.dueDate,
      linkedMeeting: item.linkedMeeting || "",
    });
    setShowForm(true);
  };

  const handleDeleteItem = (itemId) => {
    if (window.confirm("Are you sure you want to delete this action item?")) {
      setActionItems(actionItems.filter(item => item.id !== itemId));
    }
  };

  const handleSaveItem = () => {
    if (!formData.title || !formData.assignedTo || !formData.dueDate) {
      alert("Please fill in required fields: Title, Assigned To, and Due Date");
      return;
    }

    if (editingItem) {
      setActionItems(actionItems.map(item =>
        item.id === editingItem.id
          ? { ...item, ...formData }
          : item
      ));
    } else {
      const newItem = {
        ...formData,
        id: Math.max(...actionItems.map(i => i.id)) + 1,
        createdDate: new Date().toISOString().split('T')[0],
        progressNotes: [],
      };
      setActionItems([...actionItems, newItem]);
    }

    setShowForm(false);
    setFormData({
      title: "",
      description: "",
      assignedTo: "",
      assignedEmail: "",
      committee: "",
      status: "Not Started",
      priority: "Medium",
      dueDate: "",
      linkedMeeting: "",
    });
  };

  const handleAddProgressNote = (itemId) => {
    if (!progressNoteText.trim()) return;

    setActionItems(actionItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          progressNotes: [
            ...item.progressNotes,
            {
              date: new Date().toISOString().split('T')[0],
              note: progressNoteText,
              author: user?.name || "Unknown",
            },
          ],
        };
      }
      return item;
    }));

    setProgressNoteText("");
    setShowProgressNote(null);
  };

  // Filter and sort
  let filteredItems = actionItems.filter(item => {
    if (filterStatus !== "All" && item.status !== filterStatus) return false;
    if (filterPriority !== "All" && item.priority !== filterPriority) return false;
    if (filterCommittee !== "All" && item.committee !== filterCommittee) return false;
    if (filterAssignee !== "All" && item.assignedTo !== filterAssignee) return false;
    return true;
  });

  filteredItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case "dueDate":
        return new Date(a.dueDate) - new Date(b.dueDate);
      case "priority":
        const priorityOrder = { "Urgent": 0, "High": 1, "Medium": 2, "Low": 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      case "status":
        return a.status.localeCompare(b.status);
      case "created":
        return new Date(b.createdDate) - new Date(a.createdDate);
      default:
        return 0;
    }
  });

  const getStatusColor = (status) => {
    const colors = {
      "Not Started": "#a0aec0",
      "In Progress": "#4299e1",
      "Completed": "#48bb78",
      "Blocked": "#f56565",
      "Overdue": "#c53030",
    };
    return colors[status] || "#a0aec0";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      "Low": "#4299e1",
      "Medium": "#ed8936",
      "High": "#f56565",
      "Urgent": "#c53030",
    };
    return colors[priority] || "#a0aec0";
  };

  const isOverdue = (dueDate, status) => {
    if (status === "Completed") return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#2d3748",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FaTasks style={{ color: "#667eea" }} />
            Action Items
          </h1>
          <p style={{ color: "#718096", fontSize: "16px", margin: 0 }}>
            Track commitments and action items from meetings
          </p>
        </div>
        <button
          onClick={handleAddItem}
          style={{
            padding: "12px 24px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <FaPlus /> Add Action Item
        </button>
      </div>

      {/* Filters and Sort */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaFilter style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Filters:</span>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Priorities</option>
            {priorities.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>

          <select
            value={filterCommittee}
            onChange={(e) => setFilterCommittee(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Committees</option>
            {committees.map(committee => (
              <option key={committee} value={committee}>{committee}</option>
            ))}
          </select>

          <select
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Assignees</option>
            {getUniqueAssignees().map(assignee => (
              <option key={assignee} value={assignee}>{assignee}</option>
            ))}
          </select>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <FaSort style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              <option value="dueDate">By Due Date</option>
              <option value="priority">By Priority</option>
              <option value="status">By Status</option>
              <option value="created">By Created Date</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div style={{ background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center" }}>
          <div style={{ fontSize: "24px", fontWeight: "700", color: "#667eea" }}>
            {actionItems.filter(i => i.status !== "Completed").length}
          </div>
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>Open Items</div>
        </div>
        <div style={{ background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center" }}>
          <div style={{ fontSize: "24px", fontWeight: "700", color: "#f56565" }}>
            {actionItems.filter(i => isOverdue(i.dueDate, i.status)).length}
          </div>
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>Overdue</div>
        </div>
        <div style={{ background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center" }}>
          <div style={{ fontSize: "24px", fontWeight: "700", color: "#48bb78" }}>
            {actionItems.filter(i => i.status === "Completed").length}
          </div>
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>Completed</div>
        </div>
        <div style={{ background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center" }}>
          <div style={{ fontSize: "24px", fontWeight: "700", color: "#4299e1" }}>
            {actionItems.filter(i => i.status === "In Progress").length}
          </div>
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>In Progress</div>
        </div>
      </div>

      {/* Action Items List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <div
              key={item.id}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                border: isOverdue(item.dueDate, item.status) ? "2px solid #f56565" : "1px solid #e2e8f0",
              }}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", margin: "0 0 8px 0" }}>
                    {item.title}
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        padding: "4px 10px",
                        background: getStatusColor(isOverdue(item.dueDate, item.status) ? "Overdue" : item.status),
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {isOverdue(item.dueDate, item.status) ? "OVERDUE" : item.status.toUpperCase()}
                    </span>
                    <span
                      style={{
                        padding: "4px 10px",
                        background: getPriorityColor(item.priority),
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {item.priority} Priority
                    </span>
                    <span style={{ fontSize: "13px", color: "#718096" }}>
                      📁 {item.committee}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleEditItem(item)}
                      style={{
                        padding: "6px 12px",
                        background: "#f7fafc",
                        color: "#667eea",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      style={{
                        padding: "6px 12px",
                        background: "#fff5f5",
                        color: "#f56565",
                        border: "1px solid #feb2b2",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Description */}
              <p style={{ color: "#4a5568", fontSize: "14px", lineHeight: "1.6", margin: "0 0 16px 0" }}>
                {item.description}
              </p>

              {/* Meta Info */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaUser style={{ color: "#667eea", fontSize: "14px" }} />
                  <div>
                    <div style={{ fontSize: "11px", color: "#a0aec0" }}>Assigned To</div>
                    <div style={{ fontSize: "13px", color: "#2d3748", fontWeight: "500" }}>{item.assignedTo}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaCalendarDay style={{ color: isOverdue(item.dueDate, item.status) ? "#f56565" : "#667eea", fontSize: "14px" }} />
                  <div>
                    <div style={{ fontSize: "11px", color: "#a0aec0" }}>Due Date</div>
                    <div style={{ fontSize: "13px", color: isOverdue(item.dueDate, item.status) ? "#f56565" : "#2d3748", fontWeight: "500" }}>
                      {new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                </div>
                {item.linkedMeeting && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FaLink style={{ color: "#667eea", fontSize: "14px" }} />
                    <div>
                      <div style={{ fontSize: "11px", color: "#a0aec0" }}>Linked Meeting</div>
                      <div style={{ fontSize: "13px", color: "#667eea", fontWeight: "500" }}>{item.linkedMeeting}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Notes */}
              {item.progressNotes.length > 0 && (
                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#2d3748", marginBottom: "12px" }}>
                    Progress Updates
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {item.progressNotes.map((note, index) => (
                      <div key={index} style={{ padding: "8px 12px", background: "#f7fafc", borderRadius: "6px" }}>
                        <div style={{ fontSize: "11px", color: "#a0aec0", marginBottom: "4px" }}>
                          {new Date(note.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {note.author}
                        </div>
                        <div style={{ fontSize: "13px", color: "#2d3748" }}>{note.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Progress Note */}
              {showProgressNote === item.id ? (
                <div style={{ marginTop: "12px", padding: "12px", background: "#f7fafc", borderRadius: "8px" }}>
                  <textarea
                    value={progressNoteText}
                    onChange={(e) => setProgressNoteText(e.target.value)}
                    placeholder="Add a progress update..."
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                      marginBottom: "8px",
                    }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleAddProgressNote(item.id)}
                      style={{
                        padding: "6px 12px",
                        background: "#667eea",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Save Note
                    </button>
                    <button
                      onClick={() => {
                        setShowProgressNote(null);
                        setProgressNoteText("");
                      }}
                      style={{
                        padding: "6px 12px",
                        background: "#e2e8f0",
                        color: "#4a5568",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowProgressNote(item.id)}
                  style={{
                    marginTop: "12px",
                    padding: "6px 12px",
                    background: "#f7fafc",
                    color: "#667eea",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  + Add Progress Update
                </button>
              )}
            </div>
          ))
        ) : (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "60px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e2e8f0",
              textAlign: "center",
            }}
          >
            <FaTasks style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }} />
            <p style={{ color: "#718096", fontSize: "16px", marginBottom: "16px" }}>
              No action items found
            </p>
            <button
              onClick={handleAddItem}
              style={{
                padding: "10px 20px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaPlus /> Add First Action Item
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "700px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#2d3748", marginBottom: "20px" }}>
              {editingItem ? "Edit Action Item" : "Add New Action Item"}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Title */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="What needs to be done?"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Provide details about this action item..."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              {/* Two Column Layout */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                {/* Assigned To */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Assigned To *
                  </label>
                  <input
                    type="text"
                    value={formData.assignedTo}
                    onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                    placeholder="Full name"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.assignedEmail}
                    onChange={(e) => setFormData({ ...formData, assignedEmail: e.target.value })}
                    placeholder="email@nyu.edu"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              {/* Two Column Layout */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                {/* Committee */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Committee
                  </label>
                  <select
                    value={formData.committee}
                    onChange={(e) => setFormData({ ...formData, committee: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select committee</option>
                    {committees.map(committee => (
                      <option key={committee} value={committee}>{committee}</option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Due Date *
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              {/* Two Column Layout */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                {/* Status */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    {statuses.filter(s => s !== "Overdue").map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Linked Meeting */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Linked Meeting (Optional)
                </label>
                <input
                  type="text"
                  value={formData.linkedMeeting}
                  onChange={(e) => setFormData({ ...formData, linkedMeeting: e.target.value })}
                  placeholder="e.g., Steering Committee Meeting - Jan 2, 2026"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
                <button
                  onClick={handleSaveItem}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <FaSave /> Save Action Item
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#f7fafc",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

